import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { speichereKategorie, selectKategorien } from 'modules/finanzen/features/finanzenSlice';
import { mischeKategorien, EMOJI_AUSWAHL } from 'modules/finanzen/features/konstanten';
import EmojiPicker from 'modules/finanzen/components/EmojiPicker';

/** Ein auswaehlbarer Kategorie-Chip. */
function Chip({ aktiv, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
        aktiv ? 'border-brand-600 bg-brand-600 text-white' : 'border-line bg-paper text-ink hover:border-brand-400'
      }`}
    >
      {children}
    </button>
  );
}

/**
 * Kategorie-Auswahl fuer Buchungs- und Dauerauftrags-Formulare: zeigt Standard-
 * und eigene Kategorien als Chips und erlaubt das Anlegen einer neuen Kategorie
 * (Name + Emoji) direkt beim Erfassen. Neu angelegte Kategorien werden sofort
 * ausgewaehlt. DRY: von beiden Formularen genutzt.
 */
export default function KategorieAuswahl({ typ, wert, onChange }) {
  const dispatch = useDispatch();
  const eigene = useSelector(selectKategorien);
  const [anlegen, setAnlegen] = useState(false);
  const [name, setName] = useState('');
  const [icon, setIcon] = useState(EMOJI_AUSWAHL[0]);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState(null);

  const kategorien = mischeKategorien(typ, eigene);

  const hinzufuegen = async () => {
    const sauber = name.trim();
    if (!sauber) {
      setFehler('Bitte einen Namen angeben.');
      return;
    }
    if (kategorien.some((k) => k.name.toLowerCase() === sauber.toLowerCase())) {
      setFehler('Diese Kategorie gibt es bereits.');
      return;
    }
    setFehler(null);
    setLaeuft(true);
    try {
      await dispatch(speichereKategorie({ name: sauber, icon, typ })).unwrap();
      onChange(sauber);
      setName('');
      setIcon(EMOJI_AUSWAHL[0]);
      setAnlegen(false);
    } catch (ex) {
      setFehler('Anlegen fehlgeschlagen.');
    } finally {
      setLaeuft(false);
    }
  };

  return (
    <div>
      <div className="flex flex-wrap gap-2">
        {kategorien.map((k) => (
          <Chip key={k.name} aktiv={wert === k.name} onClick={() => onChange(k.name)}>
            {k.icon} {k.name}
          </Chip>
        ))}
        <button
          type="button"
          onClick={() => setAnlegen((v) => !v)}
          className="rounded-full border border-dashed border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:border-brand-400 hover:text-ink"
        >
          ＋ Neu
        </button>
      </div>

      {anlegen && (
        <div className="mt-3 rounded-xl border border-line bg-paper-soft p-3">
          <div className="mb-2 flex gap-2">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-line bg-paper text-lg">
              {icon}
            </span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name der Kategorie"
              className="w-full rounded-lg border border-line bg-paper px-3 py-2 text-ink focus:border-brand-600 focus:outline-none"
            />
          </div>
          <EmojiPicker wert={icon} onChange={setIcon} />
          {fehler && <p className="mt-2 text-sm text-red-700">{fehler}</p>}
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setAnlegen(false);
                setFehler(null);
              }}
              className="rounded-lg border border-line px-3 py-1.5 text-sm text-ink-muted transition-colors hover:bg-ink/5"
            >
              Abbrechen
            </button>
            <button
              type="button"
              onClick={hinzufuegen}
              disabled={laeuft}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white transition-colors hover:bg-brand-500 disabled:opacity-50"
            >
              {laeuft ? 'Lege an…' : 'Hinzufügen'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
